import type {
  AudienceCounts, BrandCampaignSettings, Campaign, CampaignReport, CampaignToast,
  CreditHistoryEntry, CreditPack, InboundSms, OverviewStats, RecipientRow, RewardGrant, TrackingSource,
} from '../../components/campaigns/data/types';

// ── Audience ──
export const PREVIEW_AUDIENCE_REQUEST = 'campaigns/PREVIEW_AUDIENCE_REQUEST';
export const PREVIEW_AUDIENCE_SUCCESS = 'campaigns/PREVIEW_AUDIENCE_SUCCESS';
export const PREVIEW_AUDIENCE_FAILURE = 'campaigns/PREVIEW_AUDIENCE_FAILURE';
export const CLEAR_AUDIENCE_PREVIEW = 'campaigns/CLEAR_AUDIENCE_PREVIEW';

// ── Campaigns ──
export const FETCH_CAMPAIGNS_REQUEST = 'campaigns/FETCH_CAMPAIGNS_REQUEST';
export const FETCH_CAMPAIGNS_SUCCESS = 'campaigns/FETCH_CAMPAIGNS_SUCCESS';
export const FETCH_CAMPAIGNS_FAILURE = 'campaigns/FETCH_CAMPAIGNS_FAILURE';
export const SAVE_CAMPAIGN_REQUEST = 'campaigns/SAVE_CAMPAIGN_REQUEST';
export const SAVE_CAMPAIGN_SUCCESS = 'campaigns/SAVE_CAMPAIGN_SUCCESS';
export const TRANSITION_CAMPAIGN_REQUEST = 'campaigns/TRANSITION_CAMPAIGN_REQUEST';
export const TRANSITION_CAMPAIGN_SUCCESS = 'campaigns/TRANSITION_CAMPAIGN_SUCCESS';
export const DUPLICATE_CAMPAIGN_REQUEST = 'campaigns/DUPLICATE_CAMPAIGN_REQUEST';
export const DUPLICATE_CAMPAIGN_SUCCESS = 'campaigns/DUPLICATE_CAMPAIGN_SUCCESS';
export const DELETE_CAMPAIGN_REQUEST = 'campaigns/DELETE_CAMPAIGN_REQUEST';
export const DELETE_CAMPAIGN_SUCCESS = 'campaigns/DELETE_CAMPAIGN_SUCCESS';
export const TEST_SEND_REQUEST = 'campaigns/TEST_SEND_REQUEST';
export const REVOKE_GRANTS_REQUEST = 'campaigns/REVOKE_GRANTS_REQUEST';
export const REVOKE_GRANTS_SUCCESS = 'campaigns/REVOKE_GRANTS_SUCCESS';

// ── Report ──
export const FETCH_REPORT_REQUEST = 'campaigns/FETCH_REPORT_REQUEST';
export const FETCH_REPORT_SUCCESS = 'campaigns/FETCH_REPORT_SUCCESS';
export const FETCH_REPORT_FAILURE = 'campaigns/FETCH_REPORT_FAILURE';

// ── Rewards and campaign history per customer ──
export const FETCH_ACTIVITY_REQUEST = 'campaigns/FETCH_ACTIVITY_REQUEST';
export const FETCH_ACTIVITY_SUCCESS = 'campaigns/FETCH_ACTIVITY_SUCCESS';
export const FETCH_ACTIVITY_FAILURE = 'campaigns/FETCH_ACTIVITY_FAILURE';

// ── Settings ──
export const FETCH_SETTINGS_REQUEST = 'campaigns/FETCH_SETTINGS_REQUEST';
export const FETCH_SETTINGS_SUCCESS = 'campaigns/FETCH_SETTINGS_SUCCESS';
export const FETCH_SETTINGS_FAILURE = 'campaigns/FETCH_SETTINGS_FAILURE';
export const SAVE_SETTINGS_REQUEST = 'campaigns/SAVE_SETTINGS_REQUEST';
export const SAVE_SETTINGS_SUCCESS = 'campaigns/SAVE_SETTINGS_SUCCESS';
export const BUY_CREDITS_REQUEST = 'campaigns/BUY_CREDITS_REQUEST';

// ── Opt-out review queue ──
export const FETCH_INBOUND_REQUEST = 'campaigns/FETCH_INBOUND_REQUEST';
export const FETCH_INBOUND_SUCCESS = 'campaigns/FETCH_INBOUND_SUCCESS';
export const FETCH_INBOUND_FAILURE = 'campaigns/FETCH_INBOUND_FAILURE';
export const RESOLVE_INBOUND_REQUEST = 'campaigns/RESOLVE_INBOUND_REQUEST';
export const RESOLVE_INBOUND_SUCCESS = 'campaigns/RESOLVE_INBOUND_SUCCESS';

// ── Tracking sources ──
export const FETCH_SOURCES_REQUEST = 'campaigns/FETCH_SOURCES_REQUEST';
export const FETCH_SOURCES_SUCCESS = 'campaigns/FETCH_SOURCES_SUCCESS';
export const FETCH_SOURCES_FAILURE = 'campaigns/FETCH_SOURCES_FAILURE';
export const SAVE_SOURCE_REQUEST = 'campaigns/SAVE_SOURCE_REQUEST';
export const SAVE_SOURCE_SUCCESS = 'campaigns/SAVE_SOURCE_SUCCESS';
export const DELETE_SOURCE_REQUEST = 'campaigns/DELETE_SOURCE_REQUEST';
export const DELETE_SOURCE_SUCCESS = 'campaigns/DELETE_SOURCE_SUCCESS';

// ── Overview ──
export const FETCH_OVERVIEW_REQUEST = 'campaigns/FETCH_OVERVIEW_REQUEST';
export const FETCH_OVERVIEW_SUCCESS = 'campaigns/FETCH_OVERVIEW_SUCCESS';
export const FETCH_OVERVIEW_FAILURE = 'campaigns/FETCH_OVERVIEW_FAILURE';

// ── Shared ──
export const MUTATION_FAILURE = 'campaigns/MUTATION_FAILURE';
export const TOAST_ADD = 'campaigns/TOAST_ADD';
export const TOAST_REMOVE = 'campaigns/TOAST_REMOVE';

/** Optional callbacks a screen passes with a mutation so it can navigate or close a dialog when the saga finishes. */
export interface MutationMeta<T = unknown> {
  onSuccess?: (result: T) => void;
  onFailure?: (message: string) => void;
  /** Suppress the success toast (used by draft autosave). */
  silent?: boolean;
}

export interface ResourceState {
  loading: boolean;
  loaded: boolean;
  error: string | null;
}

export interface CampaignsState {
  campaigns: Campaign[];
  campaignsStatus: ResourceState;
  /** Rewards customers hold from campaigns, and which campaigns each customer was sent and redeemed. */
  grants: RewardGrant[];
  receivedByCustomer: Record<string, string[]>;
  redeemedByCustomer: Record<string, string[]>;
  activityStatus: ResourceState;
  settings: BrandCampaignSettings | null;
  creditHistory: CreditHistoryEntry[];
  creditPacks: CreditPack[];
  settingsStatus: ResourceState;
  inbound: InboundSms[];
  inboundStatus: ResourceState;
  sources: TrackingSource[];
  sourcesStatus: ResourceState;
  reports: Record<string, CampaignReport>;
  recipients: Record<string, RecipientRow[]>;
  reportStatus: ResourceState;
  overview: OverviewStats | null;
  overviewStatus: ResourceState;
  audiencePreview: AudienceCounts | null;
  audiencePreviewLoading: boolean;
  saving: boolean;
  toasts: CampaignToast[];
}
